import { debounceTime, filter } from 'rxjs/operators';
import { EventEmitter } from '@angular/core';
import {
  ElementRef,
  Renderer2,
  ViewChild,
  AfterViewInit,
  Output
} from '@angular/core';
import { Component, forwardRef, Input, Injector } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  AbstractControl
} from '@angular/forms';
import errorsDefault from '../../../resources/errors.js';
import { FormStatus } from '../../../enum/form/form.status';

@Component({
  selector: 'tr-select',
  templateUrl: './tr-select.component.html',
  styleUrls: ['./tr-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TrSelectComponent),
      multi: true
    }
  ]
})
export class TrSelectComponent implements ControlValueAccessor, AfterViewInit {
  @Input() public options: any[];
  @Input() public indexerKey: string = 'id';
  @Input() public descriptionKey: string = 'description';
  @Input() public label: string;
  @Input() public placeholder = 'Selecione';
  @Input() public id: string;
  @Input() public tipLabel: string;
  @Input() public disabled: boolean = false;

  @ViewChild('selectElement', { static: true })
  public selectElement: ElementRef;
  @Output() public onTipLinkClicked = new EventEmitter<any>();
  @Output() public onOptionClicked = new EventEmitter<any>();

  public selectedValue: any;
  public errors = errorsDefault;
  public error: string;
  public control: AbstractControl | null;

  public ngControl: NgControl;

  private onTouchedCallback: () => any;

  constructor(private injector: Injector, private renderer: Renderer2) {}

  public ngOnInit() {
    this.ngControl = this.injector.get(NgControl);
    this.control = this.ngControl.control;

    if (!this.id) {
      throw new Error('Especifique um id para o elemento tr-select.');
    }
  }

  public ngAfterViewInit() {
    if (this.control) {
      this.control.statusChanges
        .pipe(
          filter(status => FormStatus.Invalid === status),
          debounceTime(100)
        )
        .subscribe(() => {
          this.error = this.getFirstError();
        });
    }
  }

  /**
   * Hanlder para receber o valor do form control e salvar localmente
   * @param value valor a ser usado
   */
  public writeValue(value: any): void {
    if (value !== undefined) {
      value = this.normalizeValue(value);
      this.selectedValue = value;
    }
  }

  /**
   * Propagaçõa de mudanças nos valores
   */
  // tslint:disable-next-line:no-empty
  public propagateChange = (_: any) => {};

  /**
   * Registra para enviar mudanças d
   * @param fn função a ser chamada
   */
  public registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  /**
   * Recebe um callback para ser chamado para definir o touched do form control
   * @param fn função para registro no formControl
   */
  public registerOnTouched(fn: () => any): void {
    this.onTouchedCallback = fn;
  }

  /**
   * Hanlder para mudanças no valor do model
   * @param value novo valor
   */
  public onChange(value): void {
    value = this.normalizeValue(value);
    this.propagateChange(value);
  }

  /**
   * Define se o form control está desabilitado
   * @param isDisabled desabilitado
   */
  public setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(
      this.selectElement.nativeElement,
      'disabled',
      isDisabled
    );
  }

  /**
   * Handler para o evento de onblur do select
   */
  public onBlur() {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  /**
   * Hanlder para o clique no link de dica
   * @param event evento do click
   */
  public onTipLinkClicke(event: MouseEvent) {
    this.onTipLinkClicked.emit(event);
  }

  /**
   * Handler para o clique de cada option
   * @param id id do option
   */
  public onOptionClick(id: any) {
    this.onOptionClicked.emit(id);
  }

  /**
   * Recupera o primeiro erro da lista de erros do control.
   * Caso o erro não seja do backend a key é prefixada com 'selection' para diferenciar das inputs de texto
   */
  private getFirstError() {
    if (!this.control || !this.control.errors) {
      return null;
    }

    let key = Object.keys(this.control.errors)[0];
    key = `${key}`;
    // tslint:disable-next-line:newline-per-chained-call
    key = key.match(/backendError/)
      ? key
      : `selection${key.charAt(0).toUpperCase() + key.slice(1)}`;
    return this.errors[key];
  }

  /**
   * Normaliza um valor para que o placeholder seja exibido quando o valor recebido for null
   * @param value valor a ser normalizado
   */
  private normalizeValue(value: any) {
    return value == null ? '' : value;
  }
}
